import type { SeatDetail } from './types'

export interface SeatMove {
  seatId: number
  parentSeatId: number | null
  position: number
}

const bySortOrder = (a: SeatDetail, b: SeatDetail) => a.position - b.position || a.id - b.id

/**
 * The coordinates a menu-driven move has to restate.
 *
 * `SeatsController#move` reads a re-parent that arrives without coordinates as
 * permission to re-place the box beside its new parent, which is right for a
 * box the tidy layout owns and wrong for one somebody placed by hand: Promote,
 * Demote and "Move to…" only ever change who a role reports to. A hand-placed
 * box therefore sends the spot it already occupies, and an unplaced one sends
 * nothing so the tidy layout keeps deciding.
 */
export function heldPlacementParams(seat: SeatDetail): { layout_x: number; layout_y: number } | Record<string, never> {
  if (seat.layout_x === null || seat.layout_y === null) return {}
  return { layout_x: seat.layout_x, layout_y: seat.layout_y }
}

export function siblingsOf(seats: SeatDetail[], parentSeatId: number | null): SeatDetail[] {
  return seats.filter(s => s.parent_seat_id === parentSeatId && !s.is_owners_box).sort(bySortOrder)
}

export function descendantIds(seats: SeatDetail[], seatId: number): Set<number> {
  const found = new Set<number>([seatId])
  let grew = true
  while (grew) {
    grew = false
    for (const seat of seats) {
      if (seat.parent_seat_id !== null && found.has(seat.parent_seat_id) && !found.has(seat.id)) {
        found.add(seat.id)
        grew = true
      }
    }
  }
  return found
}

/**
 * Optimistic mirror of `SeatsController#move`: pull the seat out of its old
 * sibling group, insert it at `position` in the destination group, and densely
 * renumber both groups. Keeping the two in step is what makes the dropped
 * order survive the reload.
 */
export function applySeatMove(seats: SeatDetail[], move: SeatMove): SeatDetail[] {
  const moved = seats.find(s => s.id === move.seatId)
  if (!moved) return seats

  const positions = new Map<number, { parentSeatId: number | null; position: number }>()

  const destination = siblingsOf(seats, move.parentSeatId).filter(s => s.id !== moved.id)
  const index = Math.min(Math.max(move.position, 0), destination.length)
  destination.splice(index, 0, moved)
  destination.forEach((seat, i) => positions.set(seat.id, { parentSeatId: move.parentSeatId, position: i }))

  if (moved.parent_seat_id !== move.parentSeatId) {
    siblingsOf(seats, moved.parent_seat_id)
      .filter(s => s.id !== moved.id)
      .forEach((seat, i) => positions.set(seat.id, { parentSeatId: seat.parent_seat_id, position: i }))
  }

  return seats.map(seat => {
    const next = positions.get(seat.id)
    if (!next) return seat
    if (seat.parent_seat_id === next.parentSeatId && seat.position === next.position) return seat
    return { ...seat, parent_seat_id: next.parentSeatId, position: next.position }
  })
}
